import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Globe, Search, Loader2, CheckCircle2, ShoppingCart, KeyRound, ExternalLink, Lock, Sparkles } from "lucide-react";

export default function DomainStore({ onClose }) {
  const [status, setStatus] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [ak, setAk] = useState("");
  const [sk, setSk] = useState("");
  const [connecting, setConnecting] = useState(false);

  const load = async () => { try { setStatus(await api.domainStoreStatus()); } catch {} };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("domain_checkout");
    if (!sid) return;
    window.history.replaceState({}, "", window.location.pathname);
    if (sid === "cancelled") { toast.info("Checkout cancelled — no charge made."); return; }
    toast.loading("Confirming your purchase…", { id: "dom" });
    const poll = async (n = 0) => {
      try {
        const r = await api.domainCheckoutStatus(sid);
        if (r.payment_status === "paid") {
          const o = r.order || {};
          if (o.status === "registered") toast.success(`${o.domain} is yours! 🎉 Registered and in your orders.`, { id: "dom" });
          else if (o.status === "registration_failed") toast.error(`Payment ok, but registration failed: ${o.error || "try again"}`, { id: "dom" });
          else toast.success("Payment confirmed — registering your domain…", { id: "dom" });
          return;
        }
      } catch {}
      if (n < 8) setTimeout(() => poll(n + 1), 2500);
      else toast.dismiss("dom");
    };
    poll();
  }, []);

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try { setResults(await api.domainSearch(q.trim())); }
    catch (e) { toast.error(e.response?.data?.detail || "Search failed"); }
    finally { setSearching(false); }
  };

  const connect = async () => {
    if (!ak.trim() || !sk.trim()) { toast.error("Paste both keys"); return; }
    setConnecting(true);
    try { await api.connectRegistrar(ak.trim(), sk.trim()); toast.success("Registrar connected — you can buy now"); setSk(""); setAk(""); setConnectOpen(false); await load(); if (q) search(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not connect"); }
    finally { setConnecting(false); }
  };

  const buy = async (r) => {
    if (!status?.registrar_connected) { toast.error("Connect a registrar first to enable purchases"); setConnectOpen(true); return; }
    try {
      const res = await api.domainCheckout(r.domain, r.tld, window.location.origin);
      window.location.href = res.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); }
  };

  const connected = status?.registrar_connected;

  return (
    <div className="max-w-4xl mx-auto pt-8 px-4 pb-24">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-display uppercase tracking-widest text-cyan-300 text-lg flex items-center gap-2"><Globe size={18} /> Domain Store</h2>
        <button data-testid="store-close-button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400"><X size={16} /></button>
      </div>
      <p className="text-zinc-400 text-sm mb-3 leading-relaxed">Claim the perfect web address for any idea — right here, without juggling a separate registrar. Search a name, secure it in seconds, and LifeOS handles the technical setup so you can go from idea to live presence the same day.</p>
      <ul className="text-zinc-500 text-xs mb-5 space-y-1.5">
        <li className="flex gap-2"><span className="text-cyan-400">→</span> Lock in a brand or project name before someone else takes it</li>
        <li className="flex gap-2"><span className="text-cyan-400">→</span> Spin up a home for a new venture, portfolio, campaign, or landing page</li>
        <li className="flex gap-2"><span className="text-cyan-400">→</span> Give a client, deal, or event its own polished address in minutes</li>
        <li className="flex gap-2"><span className="text-cyan-400">→</span> Everything lives inside LifeOS — no extra logins or dashboards to manage</li>
      </ul>

      {/* Registrar status / connect */}
      <div className={`rounded-xl border p-3.5 mb-5 ${connected ? "border-emerald-500/30 bg-emerald-500/[0.05]" : "border-amber-500/25 bg-amber-500/[0.05]"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-[12px]">
            {connected ? <><CheckCircle2 size={15} className="text-emerald-400" /><span className="text-emerald-300">Registrar connected (Porkbun) — purchases enabled</span></>
              : <><Lock size={15} className="text-amber-400" /><span className="text-amber-200">Connect Porkbun to enable buying. Search + pricing work now.</span></>}
          </div>
          <Button data-testid="store-connect-toggle" onClick={() => setConnectOpen((o) => !o)} className="bg-white/10 hover:bg-white/20 text-zinc-100 font-tactical font-bold uppercase text-xs">{connectOpen ? "Hide" : connected ? "Manage" : "Connect"}</Button>
        </div>
        {connectOpen && (
          <div data-testid="store-connect-form" className="mt-3 border-t border-white/10 pt-3">
            <ol className="text-[11px] text-zinc-300 space-y-1.5 mb-3 list-decimal pl-4 marker:text-cyan-400">
              <li>Create a Porkbun account and add a little balance (domains are cheap).</li>
              <li>Open <a href="https://porkbun.com/account/api" target="_blank" rel="noreferrer" className="text-cyan-300 underline inline-flex items-center gap-0.5">Account → API Access <ExternalLink size={10} /></a>, turn on API access, and create a key.</li>
              <li>Toggle <strong>"Opt In All Domains"</strong>, then copy your <strong>API Key</strong> (pk1_…) and <strong>Secret API Key</strong> (sk1_…) below.</li>
            </ol>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input data-testid="registrar-key-input" value={ak} onChange={(e) => setAk(e.target.value)} placeholder="API Key (pk1_…)" className="bg-[#0b0e14] border-white/10 text-zinc-100 text-sm" />
              <Input data-testid="registrar-secret-input" type="password" value={sk} onChange={(e) => setSk(e.target.value)} placeholder="Secret API Key (sk1_…)" className="bg-[#0b0e14] border-white/10 text-zinc-100 text-sm" />
            </div>
            <Button data-testid="registrar-connect-button" onClick={connect} disabled={connecting} className="mt-3 bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-tactical font-bold uppercase text-xs">{connecting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <KeyRound size={14} className="mr-1" />}{connecting ? "Connecting" : "Connect Porkbun"}</Button>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 mb-5">
        <div className="flex-1 relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="domain-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") search(); }} placeholder="yourbrand" className="w-full bg-[#0b0e14] border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40" />
        </div>
        <Button data-testid="domain-search-button" onClick={search} disabled={searching} className="bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-tactical font-bold uppercase text-xs px-5 py-2.5">{searching ? <Loader2 size={15} className="animate-spin" /> : "Search"}</Button>
      </div>

      {results && (
        <div data-testid="domain-results" className="space-y-2">
          {results.results.length === 0 ? (
            <div className="text-center text-zinc-500 py-10">No pricing available right now — try again in a moment.</div>
          ) : results.results.map((r) => (
            <div key={r.domain} data-testid={`domain-row-${r.tld}`} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 hover:border-cyan-300/25">
              <div className="flex items-center gap-3">
                <span className="text-sm font-tactical font-bold text-zinc-100">{r.domain}</span>
                {r.available === true && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">available</span>}
                {r.available === false && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-red-500/20 text-red-300">taken</span>}
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-sm font-tactical font-bold text-cyan-200">${r.price}<span className="text-[10px] text-zinc-500">/yr</span></div>
                  <div className="text-[9px] font-mono text-zinc-600">cost ${r.cost} + ${results.markup_usd}</div>
                </div>
                <button data-testid={`domain-buy-${r.tld}`} onClick={() => buy(r)} disabled={r.available === false} className={`flex items-center gap-1 text-[11px] font-tactical font-bold uppercase rounded-lg px-3 py-1.5 ${connected ? "bg-cyan-500 hover:bg-cyan-400 text-[#04070c]" : "bg-white/10 text-zinc-400"} disabled:opacity-40`}>{connected ? <ShoppingCart size={12} /> : <Lock size={12} />} Buy</button>
              </div>
            </div>
          ))}
          <p className="text-[11px] text-zinc-500 pt-2 flex items-center gap-1"><Sparkles size={12} className="text-cyan-400" /> Each domain you buy can auto-create a branded email + let agents build a landing page (coming next).</p>
        </div>
      )}
    </div>
  );
}
